import React, { useContext, useEffect, useMemo, useState } from 'react';
import type { Translations } from '@ckeditor/ckeditor5-utils';
import { AppContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { isAdmin, isApplicant, isRecruiter } from '~/jobiqo/lib/auth';
import { AppContextInterface } from '~/jobiqo/models/app';
import { User } from '~/jobiqo/models/user';
import { Textarea } from '~/vcl/atoms/01-forms/04-textarea';
import 'ckeditor5/ckeditor5.css';
import styles from './wysiwyg.module.scss';

interface EditorModules {
  CKEditor: any;
  ClassicEditor: any;
  plugins: any[];
  translations?: Translations;
}

export interface WYSIWYGEditorProps {
  id: string;
  /**
   * The content to be displayed in the editor.
   */
  content: string;
  /**
   * Text that will be shown inside the field until a value is entered.
   */
  placeholder?: string;
  /**
   * The number of rows (textarea style) we want for the input.
   *
   * @default "3"
   */
  rows?: number;
  /**
   * The max length of the textarea.
   */
  maxLength?: number;
  /**
   * Onchange event for the editor.
   */
  onChange?: (event, editor) => void;
  /**
   * Blur event for the editor.
   */
  onBlur?: (event, editor) => void;
  /**
   * onFocus event for the editor.
   */
  onFocus?: (event, editor) => void;
}

/**
 * Return the right editor for the appropriate role.
 *
 * @param user
 *  The current user.
 */
const getConfigPerRole = (user: User | undefined, USER_CONFIG) => {
  if (!user) {
    return USER_CONFIG.ANON.editor;
  }

  if (isApplicant(user) || isRecruiter(user)) {
    return USER_CONFIG.APPLICANT.editor;
  }

  if (isRecruiter(user)) {
    return USER_CONFIG.RECRUITER.editor;
  }

  if (isAdmin(user)) {
    return USER_CONFIG.ADMIN.editor;
  }
  return null;
};

/**
 * Renders a WYSIWYG editor for editing complex textareas. WYSIWYG should be used when text is accepted from the
 * user as some sort of HTML (filtered or not).
 */
export const WYSIWYGEditor = React.forwardRef(
  (
    {
      id,
      content = '',
      placeholder = '',
      onChange,
      onBlur,
      onFocus,
      rows = 3,
      maxLength,
    }: WYSIWYGEditorProps,
    ref,
  ) => {
    const userContext: UserContextInterface = useContext(UserContext);
    const { locale }: AppContextInterface = useContext(AppContext);
    const { USER } = useFrontendConfig();
    const [editorModules, setEditorModules] = useState<EditorModules | null>(
      null,
    );

    const { CKEditor, ClassicEditor } = editorModules || {};
    // As LATAM Spanish (es-419) translation do not exist for CKEditor, we need to use standard Spanish (es)
    // translation instead. There is Colombian translation available (es-co), but it is not fully translated (some
    // translations are missing), and it is better to have full translation with some differences than incomplete
    // translation.
    const normalizedLocale = useMemo(
      () => (locale === 'en' ? 'en-gb' : locale === 'es-419' ? 'es' : locale),
      [locale],
    );

    useEffect(() => {
      let isMounted = true;

      Promise.all([
        import('@ckeditor/ckeditor5-react'),
        import('ckeditor5'),
        import(`ckeditor5/translations/${normalizedLocale}.js`).catch(() => {
          if (isMounted) {
            console.error(
              `CKEditor does not support ${locale} language config.`,
            );
          }
          return null;
        }),
      ])
        .then(([reactModule, ckeditorModule, translationModule]) => {
          if (!isMounted) {
            return;
          }

          setEditorModules({
            CKEditor: reactModule.CKEditor,
            ClassicEditor: ckeditorModule.ClassicEditor,
            plugins: [
              ckeditorModule.Autoformat,
              ckeditorModule.BlockQuote,
              ckeditorModule.Bold,
              ckeditorModule.Essentials,
              ckeditorModule.Heading,
              ckeditorModule.Indent,
              ckeditorModule.Italic,
              ckeditorModule.Link,
              ckeditorModule.List,
              ckeditorModule.MediaEmbed,
              ckeditorModule.Paragraph,
              ckeditorModule.PasteFromOffice,
              ckeditorModule.Table,
              ckeditorModule.TableToolbar,
            ],
            translations: translationModule?.default,
          });
        })
        .catch((error) => {
          if (!isMounted) {
            return;
          }

          console.error('Failed to load CKEditor.', error);
        });

      return () => {
        isMounted = false;
      };
    }, [locale, normalizedLocale]);

    useEffect(() => {
      // Set CSS variable, so we can calculate editor height in css file.
      document.documentElement.style.setProperty(
        '--ck-editor-rows',
        rows.toString(),
      );
    }, [rows]);

    const configPerRole = getConfigPerRole(userContext.user, USER);
    const editorConfig = useMemo(
      () => ({
        ...configPerRole,
        licenseKey: 'GPL',
        plugins: editorModules?.plugins || [],
        ui: {
          height: '300px',
          viewportOffset: {
            top: 0,
          },
        },
        toolbar: {
          items: configPerRole.toolbar,
        },
        language: normalizedLocale,
        ...(editorModules?.translations
          ? { translations: editorModules.translations }
          : {}),
        root: {
          ...configPerRole.root,
          placeholder,
        },
      }),
      [configPerRole, editorModules, normalizedLocale, placeholder],
    );

    return (
      <div className={styles.editorWrapper}>
        {CKEditor && ClassicEditor ? (
          <CKEditor
            key={id}
            ref={ref}
            editor={ClassicEditor}
            onReady={(editor) => {
              // We need to hold a reference to the ckeditor globally to use in tests.
              (window as any).CKEDITOR = editor;
            }}
            data={content}
            config={editorConfig}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            onError={(error) => {
              console.error('CKEditor encountered an error.', error);
            }}
          />
        ) : (
          <Textarea
            key={id}
            rows={rows}
            disabled={true}
            maxLength={maxLength}
          />
        )}
      </div>
    );
  },
);
export default WYSIWYGEditor;
